import {
    Box,
    Button,
    Flex,
    HStack,
    Text,
    Tooltip,
    Icon,
    Badge,
    VStack,
} from '@chakra-ui/react';
import { TbInfoCircle, TbX } from 'react-icons/tb';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
    closestCorners,
    pointerWithin,
    type CollisionDetection,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLayoutEffect, useRef, useState } from 'react';
import {
    IGNORE_COLUMN_ID,
    UNASSIGNED_COLUMN_ID,
    type ImportColumnMapping,
    type ImportTargetField,
} from '../../functions/import/fuzzyColumnMatch';

export { UNASSIGNED_COLUMN_ID, IGNORE_COLUMN_ID };

const END_SUFFIX = '::__end';

function endDroppableId(columnId: string): string {
    return `${columnId}${END_SUFFIX}`;
}

function columnIdFromEndDroppable(overId: string): string | null {
    if (!overId.endsWith(END_SUFFIX)) return null;
    return overId.slice(0, -END_SUFFIX.length);
}

const collisionDetection: CollisionDetection = (args) => {
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) return pointerHits;
    return closestCorners(args);
};

interface ImportColumnMapperProps {
    sourceColumns: string[];
    targetFields: ImportTargetField[];
    mapping: ImportColumnMapping;
    onChange: (mapping: ImportColumnMapping) => void;
}

function SortablePill({
    id,
    label,
    orderNumber,
    onUnassign,
}: {
    id: string;
    label: string;
    orderNumber?: number;
    onUnassign?: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab',
    };

    return (
        <Badge
            ref={setNodeRef}
            style={style}
            px={2}
            py={1}
            borderRadius="md"
            bg="gray.100"
            color="gray.800"
            fontWeight="medium"
            fontSize="xs"
            maxW="100%"
            whiteSpace="normal"
            textAlign="left"
            display="flex"
            alignItems="flex-start"
            gap={1}
        >
            <Box as="span" flex={1} minW={0} {...listeners} {...attributes}>
                {orderNumber != null ? `${orderNumber}. ` : ''}
                {label}
            </Box>
            {onUnassign ? (
                <Box
                    as="button"
                    type="button"
                    aria-label={`Unassign ${label}`}
                    flexShrink={0}
                    lineHeight={1}
                    color="gray.500"
                    _hover={{ color: 'gray.800' }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onUnassign();
                    }}
                >
                    <Icon as={TbX as React.ElementType} boxSize={3} />
                </Box>
            ) : null}
        </Badge>
    );
}

function EndDropZone({ columnId, tall }: { columnId: string; tall?: boolean }) {
    const { setNodeRef, isOver } = useDroppable({ id: endDroppableId(columnId) });
    return (
        <Box
            ref={setNodeRef}
            mt={1}
            minH={tall ? '72px' : '44px'}
            flex={tall ? 1 : undefined}
            borderRadius="md"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor={isOver ? 'blue.400' : 'transparent'}
            bg={isOver ? 'blue.50' : 'transparent'}
            flexShrink={0}
        />
    );
}

function DroppableColumnShell({
    id,
    title,
    description,
    required,
    pillIds,
    sourceLabelById,
    showOrderNumbers,
    minW,
    maxW,
    flex,
    headerAction,
    onUnassignPill,
    onClear,
}: {
    id: string;
    title: string;
    description?: string;
    required?: boolean;
    pillIds: string[];
    sourceLabelById: Record<string, string>;
    showOrderNumbers: boolean;
    minW?: string;
    maxW?: string;
    flex?: number | string;
    headerAction?: React.ReactNode;
    onUnassignPill?: (pillId: string) => void;
    /** Moves all mapped source columns from this card to Unassigned. */
    onClear?: () => void;
}) {
    const { setNodeRef, isOver } = useDroppable({ id });

    return (
        <Box
            ref={setNodeRef}
            borderWidth="1px"
            borderColor={isOver ? 'blue.400' : 'gray.200'}
            borderRadius="md"
            bg={isOver ? 'blue.50' : 'white'}
            p={3}
            minH="140px"
            minW={minW}
            maxW={maxW}
            flex={flex}
            flexShrink={flex != null ? 1 : 0}
            display="flex"
            flexDirection="column"
        >
            <HStack mb={2} spacing={1} align="flex-start" justify="space-between">
                <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.800"
                    noOfLines={2}
                    minW={0}
                    flex={1}
                >
                    {title}
                </Text>
                <HStack spacing={0.5} align="flex-start" flexShrink={0}>
                    {required ? (
                        <Text as="span" color="red.500" fontSize="sm" fontWeight="semibold" lineHeight="1.25rem">
                            *
                        </Text>
                    ) : null}
                    {description ? (
                        <Tooltip label={description} hasArrow placement="top" openDelay={200}>
                            <Box
                                as="span"
                                display="inline-flex"
                                color="gray.500"
                                cursor="help"
                                mt={0.5}
                            >
                                <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                            </Box>
                        </Tooltip>
                    ) : null}
                    {headerAction}
                </HStack>
            </HStack>
            <SortableContext items={pillIds} strategy={verticalListSortingStrategy}>
                <Flex gap={1.5} flexDirection="column" flex={1} minH="48px">
                    {pillIds.length === 0 ? (
                        <Text fontSize="xs" color="gray.400">
                            Drag columns here
                        </Text>
                    ) : (
                        pillIds.map((pillId, index) => (
                            <SortablePill
                                key={pillId}
                                id={pillId}
                                label={sourceLabelById[pillId] ?? pillId}
                                orderNumber={showOrderNumbers ? index + 1 : undefined}
                                onUnassign={
                                    onUnassignPill ? () => onUnassignPill(pillId) : undefined
                                }
                            />
                        ))
                    )}
                    <EndDropZone columnId={id} tall={pillIds.length === 0} />
                </Flex>
            </SortableContext>
            {onClear ? (
                <Button
                    mt={2}
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    alignSelf="stretch"
                    isDisabled={pillIds.length === 0}
                    onClick={onClear}
                >
                    Clear
                </Button>
            ) : null}
        </Box>
    );
}

export function ImportColumnMapper({
    sourceColumns,
    targetFields,
    mapping,
    onChange,
}: ImportColumnMapperProps) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const sourceLabelById: Record<string, string> = {};
    sourceColumns.forEach((col, index) => {
        sourceLabelById[`src:${index}`] = col;
    });

    const nameToPillId = new Map(sourceColumns.map((col, i) => [col, `src:${i}`]));
    const pillIdToName = new Map(sourceColumns.map((col, i) => [`src:${i}`, col]));

    const columnOrder = [
        UNASSIGNED_COLUMN_ID,
        ...targetFields.map((f) => f.id),
        IGNORE_COLUMN_ID,
    ];

    const pillsByColumn: Record<string, string[]> = {};
    columnOrder.forEach((colId) => {
        const names = mapping[colId] ?? [];
        pillsByColumn[colId] = names
            .map((name) => nameToPillId.get(name))
            .filter((id): id is string => !!id);
    });

    const findColumnOfPill = (pillId: string): string | null => {
        for (const colId of columnOrder) {
            if (pillsByColumn[colId]?.includes(pillId)) return colId;
        }
        return null;
    };

    const resolveTargetColumn = (overId: string): string | null => {
        const fromEnd = columnIdFromEndDroppable(overId);
        if (fromEnd && columnOrder.includes(fromEnd)) return fromEnd;
        if (columnOrder.includes(overId)) return overId;
        return findColumnOfPill(overId);
    };

    const handleUnassignPill = (pillId: string) => {
        const fromCol = findColumnOfPill(pillId);
        if (!fromCol || fromCol === UNASSIGNED_COLUMN_ID) return;
        const sourceName = pillIdToName.get(pillId);
        if (!sourceName) return;
        const next: ImportColumnMapping = { ...mapping };
        columnOrder.forEach((colId) => {
            next[colId] = [...(mapping[colId] ?? [])];
        });
        next[fromCol] = (next[fromCol] ?? []).filter((n) => n !== sourceName);
        next[UNASSIGNED_COLUMN_ID] = [...(next[UNASSIGNED_COLUMN_ID] ?? []), sourceName];
        onChange(next);
    };

    const handleClearTarget = (fieldId: string) => {
        const names = [...(mapping[fieldId] ?? [])];
        if (names.length === 0) return;
        const next: ImportColumnMapping = { ...mapping };
        columnOrder.forEach((colId) => {
            next[colId] = [...(mapping[colId] ?? [])];
        });
        next[fieldId] = [];
        next[UNASSIGNED_COLUMN_ID] = [...(next[UNASSIGNED_COLUMN_ID] ?? []), ...names];
        onChange(next);
    };

    const handleIgnoreAll = () => {
        const unassigned = [...(mapping[UNASSIGNED_COLUMN_ID] ?? [])];
        if (unassigned.length === 0) return;
        const next: ImportColumnMapping = { ...mapping };
        columnOrder.forEach((id) => {
            next[id] = [...(mapping[id] ?? [])];
        });
        next[UNASSIGNED_COLUMN_ID] = [];
        next[IGNORE_COLUMN_ID] = [...(next[IGNORE_COLUMN_ID] ?? []), ...unassigned];
        onChange(next);
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over) return;
        const pillId = String(active.id);
        const fromCol = findColumnOfPill(pillId);
        if (!fromCol) return;

        const overId = String(over.id);
        const toCol = resolveTargetColumn(overId);
        if (!toCol) return;

        const sourceName = pillIdToName.get(pillId);
        if (!sourceName) return;

        const next: ImportColumnMapping = {};
        columnOrder.forEach((id) => {
            next[id] = [...(mapping[id] ?? [])];
        });

        const droppedOnEnd = Boolean(columnIdFromEndDroppable(overId));
        const droppedOnColumnShell = columnOrder.includes(overId);

        if (fromCol === toCol) {
            if (droppedOnEnd || droppedOnColumnShell) {
                const oldIndex = next[fromCol].indexOf(sourceName);
                if (oldIndex < 0) return;
                const without = next[fromCol].filter((n) => n !== sourceName);
                next[fromCol] = [...without, sourceName];
                onChange(next);
                return;
            }
            const oldIndex = next[fromCol].indexOf(sourceName);
            const overName = pillIdToName.get(overId);
            if (!overName || oldIndex < 0) return;
            const newIndex = next[fromCol].indexOf(overName);
            if (newIndex < 0 || oldIndex === newIndex) return;
            next[fromCol] = arrayMove(next[fromCol], oldIndex, newIndex);
            onChange(next);
            return;
        }

        next[fromCol] = next[fromCol].filter((n) => n !== sourceName);
        if (droppedOnEnd || droppedOnColumnShell) {
            next[toCol] = [...next[toCol], sourceName];
        } else {
            const overName = pillIdToName.get(overId);
            const insertAt = overName ? next[toCol].indexOf(overName) : -1;
            if (insertAt >= 0) {
                next[toCol] = [
                    ...next[toCol].slice(0, insertAt),
                    sourceName,
                    ...next[toCol].slice(insertAt),
                ];
            } else {
                next[toCol] = [...next[toCol], sourceName];
            }
        }
        onChange(next);
    };

    const activeLabel = activeId ? sourceLabelById[activeId] : null;
    const colMinW = '160px';
    const unassignedCount = (mapping[UNASSIGNED_COLUMN_ID] ?? []).length;

    const wrapperRef = useRef<HTMLDivElement>(null);
    const targetsRowRef = useRef<HTMLDivElement>(null);
    const [contentW, setContentW] = useState<number | null>(null);

    // Expand to max available (95vw content area) so cards can reflow, then shrink
    // wrapper (+ modal via fit-content) to the first-row target-card span — no blank margin.
    useLayoutEffect(() => {
        const GAP_PX = 12; // matches Flex gap={3}
        const CARD_PX = 160;

        const firstRowSpan = (targetsRow: HTMLElement): number => {
            const targetCards = Array.from(targetsRow.children) as HTMLElement[];
            if (targetCards.length === 0) return 0;
            const tops = targetCards.map((el) => el.getBoundingClientRect().top);
            const firstRowTop = Math.min(...tops);
            const firstRowCards = targetCards.filter(
                (_, i) => Math.abs(tops[i] - firstRowTop) < 2,
            );
            const cards = firstRowCards.length > 0 ? firstRowCards : targetCards;
            const left = Math.min(...cards.map((el) => el.getBoundingClientRect().left));
            const right = Math.max(...cards.map((el) => el.getBoundingClientRect().right));
            return Math.ceil(right - left);
        };

        const measure = () => {
            const wrapper = wrapperRef.current;
            const targetsRow = targetsRowRef.current;
            if (!wrapper || !targetsRow) return;

            const modalBody = wrapper.closest('.chakra-modal__body') as HTMLElement | null;
            const bodyStyle = modalBody ? window.getComputedStyle(modalBody) : null;
            const padX = bodyStyle
                ? (parseFloat(bodyStyle.paddingLeft) || 0) +
                  (parseFloat(bodyStyle.paddingRight) || 0)
                : 48;
            const maxAvail = Math.max(
                CARD_PX,
                Math.floor(window.innerWidth * 0.95) - Math.ceil(padX),
            );

            // Phase 1: expand so wrap uses full available viewport width.
            wrapper.style.width = `${maxAvail}px`;

            // How many 160px cards fit in maxAvail with gap={3}
            const fitCount = Math.max(
                1,
                Math.floor((maxAvail + GAP_PX) / (CARD_PX + GAP_PX)),
            );
            const idealSpan = fitCount * CARD_PX + (fitCount - 1) * GAP_PX;
            const measuredSpan = firstRowSpan(targetsRow);
            const span = Math.min(maxAvail, Math.max(measuredSpan, Math.min(idealSpan, maxAvail)));

            wrapper.style.width = `${span}px`;
            setContentW((prev) => (prev === span ? prev : span));
        };

        measure();
        const ro = new ResizeObserver(measure);
        if (wrapperRef.current) ro.observe(wrapperRef.current.parentElement ?? wrapperRef.current);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [targetFields.length, mapping]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <VStack
                ref={wrapperRef}
                align="stretch"
                spacing={3}
                pb={2}
                w={contentW != null ? `${contentW}px` : '100%'}
                maxW="100%"
            >
                <Flex gap={3} align="stretch" w="100%">
                    <DroppableColumnShell
                        id={UNASSIGNED_COLUMN_ID}
                        title="Unassigned"
                        description="Every source column must be mapped to a field or moved to Ignore before you can submit."
                        pillIds={pillsByColumn[UNASSIGNED_COLUMN_ID] ?? []}
                        sourceLabelById={sourceLabelById}
                        showOrderNumbers={false}
                        flex={1}
                        headerAction={
                            <Button
                                size="xs"
                                variant="outline"
                                flexShrink={0}
                                isDisabled={unassignedCount === 0}
                                onClick={handleIgnoreAll}
                            >
                                Ignore All
                            </Button>
                        }
                    />
                    <DroppableColumnShell
                        id={IGNORE_COLUMN_ID}
                        title="Ignore"
                        description="Columns here are discarded during import."
                        pillIds={pillsByColumn[IGNORE_COLUMN_ID] ?? []}
                        sourceLabelById={sourceLabelById}
                        showOrderNumbers={false}
                        flex={1}
                    />
                </Flex>
                <Flex
                    ref={targetsRowRef}
                    gap={3}
                    align="stretch"
                    flexWrap="wrap"
                    w="100%"
                >
                    {targetFields.map((field) => (
                        <DroppableColumnShell
                            key={field.id}
                            id={field.id}
                            title={field.label}
                            description={field.description}
                            required={field.required}
                            pillIds={pillsByColumn[field.id] ?? []}
                            sourceLabelById={sourceLabelById}
                            showOrderNumbers
                            minW={colMinW}
                            maxW={colMinW}
                            onUnassignPill={handleUnassignPill}
                            onClear={() => handleClearTarget(field.id)}
                        />
                    ))}
                </Flex>
            </VStack>
            <DragOverlay>
                {activeLabel ? (
                    <Badge
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg="blue.100"
                        color="gray.800"
                        fontWeight="medium"
                        fontSize="xs"
                    >
                        {activeLabel}
                    </Badge>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
